import { useMemo } from 'react'

import { SimpleModal } from '@sequencehq/core-components'
import { toMoney } from '@sequencehq/utils'
import { MINUTES } from '@sequencehq/utils/dates'
import { useQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

import { Invoice } from 'Invoices/types'

import { useRecordPayment } from './useRecordPayment'
import {
  getInvoiceBalanceDetails,
  isPositiveAmount,
  toPaymentDateIso
} from './recordPayment.helpers'

/**
 * Confirm dialog for "Mark as paid": skips the drawer and records a payment for
 * the full outstanding balance, clearing the invoice. Reuses the record-payment
 * confirm test id so both entry points share one confirmation affordance.
 */
export const RecordFullPaymentModal = ({
  invoiceId,
  onClose
}: {
  invoiceId: string
  onClose: () => void
}) => {
  const invoiceQuery = useQuery(
    dashboardv99990101Client.getInvoice,
    { id: invoiceId },
    { staleTime: 1 * MINUTES }
  )

  const invoice = invoiceQuery.data

  if (!invoice) {
    return null
  }

  return <FullPaymentConfirm invoice={invoice} onClose={onClose} />
}

const FullPaymentConfirm = ({
  invoice,
  onClose
}: {
  invoice: Invoice
  onClose: () => void
}) => {
  const { currency } = invoice
  const { balance } = getInvoiceBalanceDetails(invoice)

  const { recordPayment } = useRecordPayment({ invoiceId: invoice.id })

  const invoiceReference = invoice.invoiceNumber ?? 'this invoice'

  // One key per session, so retrying an ambiguous failure dedupes.
  const idempotencyKey = useMemo(() => crypto.randomUUID(), [])

  const onConfirm = async (): Promise<boolean> => {
    const recorded = await recordPayment({
      amount: balance,
      currency,
      paymentDate: toPaymentDateIso(new Date()),
      invoiceBalanceVersion: invoice.balanceVersion ?? 0,
      idempotencyKey
    })

    if (recorded) {
      onClose()
    }

    return recorded
  }

  return (
    <SimpleModal
      isOpen
      onClose={onClose}
      variant="dialog"
      title="Mark as paid"
      submitButtonText="Mark as paid"
      submittingButtonText="Marking as paid..."
      submitDisabled={!isPositiveAmount(balance)}
      onSubmit={onConfirm}
      data-testid="record-payment.confirm"
    >
      Marking {invoiceReference} as paid will record a payment of{' '}
      {toMoney({ value: balance, currency })} to clear the outstanding balance.
    </SimpleModal>
  )
}
